/**
 * Dealer lists — Alex’s one place for imported lists and live locators.
 *
 * A fresh imported list (≤ 180 days) is checked first, then the manufacturer
 * locator. A published PDF stays beside the locator: dealers who are not
 * online retailers can still match the file. A PDF older than 180 days is
 * saved for approval instead of verifying on its own. A miss is not a rejection.
 */

import React, { useMemo, useState } from 'react';
import { Alert, Box, ButtonBase, Skeleton, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { brandDealerDirectoryApi } from '../../services/brandDealerDirectoryApi';
import { CheckStoresResult, dealerLocatorApi, LocatorLookupResult } from '../../services/dealerLocatorApi';
import { useToast } from '../../contexts/ToastContext';
import { ConfirmDialog, DateField, FormDialog, InlineMessage, StatusChip } from '../../components/ui';
import { bvCardSx, bvColor, bvContentSx, bvPageSx, bvRadius, bvTransition, bvType, tabular } from './brandVendorsTokens';
import {
  BvButton,
  BvEmptyState,
  BvPageHeader,
  BvSearchInput,
  IconArrowOut,
  IconChevron,
  LogoFrame,
} from './components/BrandVendorsPrimitives';
import { CombinedRow, dealerKindLabel, listIsFresh, mergeRows, publicLinkLabel, tierCodesSummary } from './dealerListRows';
import { resolveBrandLogo } from '../../utils/vendorLogos';

const GRID = 'minmax(180px,1.3fr) minmax(180px,1.5fr) minmax(120px,0.9fr) minmax(90px,0.7fr) minmax(348px,2fr)';
const ACTIONS_GRID = 'minmax(56px,0.85fr) minmax(64px,0.9fr) minmax(56px,0.85fr) minmax(110px,1.4fr)';

const kindLabel = dealerKindLabel;

/** The boilerplate sentence every proof-only brand carries; shown once in the legend instead of per row. */
const isLetterOnlyBoilerplate = (note: string) => /^No public authorized-dealer list/i.test(note.trim());

export const BrandVendorsDealerListsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<'all' | 'ready' | 'letter' | 'ingested'>('all');
  const [expandedBrand, setExpandedBrand] = useState<string | null>(null);
  const [lastProbe, setLastProbe] = useState<Record<string, LocatorLookupResult>>({});
  const [lastCheck, setLastCheck] = useState<Record<string, CheckStoresResult>>({});
  const [uploadFor, setUploadFor] = useState<CombinedRow | null>(null);
  const [approveTarget, setApproveTarget] = useState<{
    brandId: string;
    organizationId: string;
    organizationName: string;
    kind: 'stale' | 'partial';
    notes?: string;
  } | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [listDate, setListDate] = useState('');

  const directoryQuery = useQuery({
    queryKey: ['admin-brand-dealer-directory'],
    queryFn: () => brandDealerDirectoryApi.list(),
  });
  const locatorQuery = useQuery({
    queryKey: ['dealer-locators'],
    queryFn: () => dealerLocatorApi.list(),
  });

  const rows = useMemo(() => {
    const merged = mergeRows(
      directoryQuery.data?.sources || [],
      directoryQuery.data?.imports || {},
      locatorQuery.data || [],
      {
        claims: directoryQuery.data?.claims,
        stores: directoryQuery.data?.stores,
      },
      directoryQuery.data?.letterOnlyBrands || [],
    );
    return merged.filter(row => {
      if (filter === 'ingested' && !row.ingested) return false;
      if (filter === 'ready' && !row.claimTime && !row.imported) return false;
      if (filter === 'letter' && (row.claimTime || row.imported || row.kind === 'list' || row.kind === 'list_locator')) return false;
      if (!query.trim()) return true;
      const hay = `${row.brandName} ${row.notes} ${row.covers.join(' ')}`.toLowerCase();
      return hay.includes(query.trim().toLowerCase());
    });
  }, [directoryQuery.data, locatorQuery.data, filter, query]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-brand-dealer-directory'] });
    queryClient.invalidateQueries({ queryKey: ['dealer-locators'] });
  };

  const fetchMutation = useMutation({
    mutationFn: (brandId: string) => brandDealerDirectoryApi.fetchList(brandId),
    onSuccess: body => {
      invalidate();
      showToast(body.message || 'Imported', 'success');
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  });

  const uploadMutation = useMutation({
    mutationFn: () => {
      if (!uploadFor || !file) throw new Error('Attach the list first');
      return brandDealerDirectoryApi.upload(uploadFor.brandId, file, { listDate, sourceUrl: uploadFor.publicUrl });
    },
    onSuccess: body => {
      invalidate();
      setUploadFor(null);
      setFile(null);
      setListDate('');
      showToast(body.message || 'Imported', 'success');
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  });

  const sweepMutation = useMutation({
    mutationFn: () => brandDealerDirectoryApi.sweep(),
    onSuccess: body => {
      invalidate();
      showToast(body.message || 'Checked stores against imported lists', 'success');
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  });

  const probeMutation = useMutation({
    mutationFn: (brandId: string) => dealerLocatorApi.probe(brandId),
    onSuccess: result => {
      setLastProbe(current => ({ ...current, [result.result.brandId]: result.result }));
      setExpandedBrand(result.result.brandId);
      showToast(
        result.result.matched
          ? `The test store matched on ${result.result.candidate?.name || result.source.brandName}`
          : result.result.notes,
        result.result.matched ? 'success' : 'info',
      );
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  });

  const checkMutation = useMutation({
    mutationFn: (brandId: string) => dealerLocatorApi.checkStores(brandId),
    onSuccess: result => {
      invalidate();
      setLastCheck(current => ({ ...current, [result.brandId]: result }));
      setExpandedBrand(result.brandId);
      const verified = result.results.filter(row => row.matched).map(row => row.organizationName);
      const unchecked = result.results.filter(row => row.outcome === 'unavailable').map(row => row.organizationName);
      const open = result.results
        .filter(row => !row.matched && row.outcome !== 'unavailable')
        .map(row => row.organizationName);
      const parts = [
        `Checked ${result.checked} store claim${result.checked === 1 ? '' : 's'}`,
        verified.length ? `verified ${verified.join(', ')}` : `verified ${result.verified}`,
      ];
      if (unchecked.length) parts.push(`could not check: ${unchecked.join(', ')}`);
      if (open.length) parts.push(`still open: ${open.join(', ')}`);
      if (result.listFetchError) parts.push('the published list could not be fetched');
      showToast(`${parts.join('; ')}.`, result.listFetchError || unchecked.length ? 'info' : 'success');
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  });

  const approveMutation = useMutation({
    mutationFn: ({
      brandId,
      organizationId,
      kind,
      notes,
    }: {
      brandId: string;
      organizationId: string;
      kind: 'stale' | 'partial';
      notes?: string;
    }) =>
      kind === 'partial'
        ? dealerLocatorApi.approvePartialMatch(
            brandId,
            organizationId,
            notes || `Approved partial list match for ${organizationId}`,
          )
        : dealerLocatorApi.approveStaleList(brandId, organizationId),
    onSuccess: (_data, variables) => {
      invalidate();
      setApproveTarget(null);
      showToast(
        variables.kind === 'partial' ? 'Approved the partial list match' : 'Approved the stale list match',
        'success',
      );
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  });

  const busyBrand =
    fetchMutation.isPending
      ? fetchMutation.variables
      : probeMutation.isPending
        ? probeMutation.variables
        : checkMutation.isPending
          ? checkMutation.variables
          : null;

  const importedCount = Object.keys(directoryQuery.data?.imports || {}).length;
  const loadError = directoryQuery.error || locatorQuery.error;
  const isLoading = directoryQuery.isLoading || locatorQuery.isLoading;
  const registryRows = rows.filter(row => !row.noRegistry);
  const fallbackRows = rows.filter(row => row.noRegistry);

  function renderRow(row: CombinedRow) {
    const stores = row.stores || [];
    const open = row.claims.unverified + row.claims.pending;
    const expanded = expandedBrand === row.brandId;
    const canCheck = row.claimTime || Boolean(row.imported);
    const fresh = listIsFresh(row.imported);
    const probe = lastProbe[row.brandId];
    const canExpand = stores.length > 0 || row.claimTime || Boolean(probe);
    return (
      <Box key={row.brandId} sx={{ borderBottom: `1px solid ${bvColor.borderSubtle}` }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0,1fr)', md: GRID },
            gap: '12px',
            px: '16px',
            py: '14px',
            alignItems: { xs: 'stretch', md: 'center' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
            <LogoFrame
              name={row.brandName}
              url={resolveBrandLogo(row.brandName, row.logoUrl) || row.logoUrl || null}
              size={32}
              artSize={24}
              monogramSize={12}
            />
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ ...bvType.tablePrimary, color: bvColor.ink }}>{row.brandName}</Box>
              {row.covers.length > 0 && (
                <Box sx={{ ...bvType.caption, color: bvColor.ink3 }}>also {row.covers.join(', ')}</Box>
              )}
            </Box>
          </Box>
          <Box>
            <Box sx={{ ...bvType.eyebrow, display: { xs: 'block', md: 'none' }, mb: '2px' }}>How we verify</Box>
            <Box sx={{ ...bvType.body, color: bvColor.ink }}>{kindLabel[row.kind]}</Box>
            <Box sx={{ display: 'flex', gap: '10px', flexWrap: 'wrap', mt: '2px' }}>
              {row.publicUrl && (
                <Box
                  component="a"
                  href={row.publicUrl}
                  target="_blank"
                  rel="noreferrer"
                  sx={{ ...bvType.caption, color: bvColor.ink, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                >
                  {publicLinkLabel(row)}
                  <IconArrowOut size={12} />
                </Box>
              )}
                      {row.pdfUrl && row.pdfUrl !== row.publicUrl && (
                        <Box
                          component="a"
                          href={row.pdfUrl}
                          target="_blank"
                          rel="noreferrer"
                          sx={{ ...bvType.caption, color: bvColor.ink, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                        >
                          Open PDF
                          <IconArrowOut size={12} />
                        </Box>
                      )}
                      {row.locatorUrl && row.locatorUrl !== row.publicUrl && (
                <Box
                  component="a"
                  href={row.locatorUrl}
                  target="_blank"
                  rel="noreferrer"
                  sx={{ ...bvType.caption, color: bvColor.ink, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                >
                  Open locator
                  <IconArrowOut size={12} />
                </Box>
              )}
            </Box>
            {row.notes && !(row.kind === 'letter' && isLetterOnlyBoilerplate(row.notes)) && (
              <Box sx={{ ...bvType.caption, color: bvColor.ink3, mt: '2px' }}>{row.notes}</Box>
            )}
            {tierCodesSummary(row) && (
              <Box sx={{ ...bvType.caption, color: bvColor.ink3, mt: '2px' }}>Tiers read: {tierCodesSummary(row)}</Box>
            )}
          </Box>
          <Box>
            <Box sx={{ ...bvType.eyebrow, display: { xs: 'block', md: 'none' }, mb: '2px' }}>Imported list</Box>
            <Box sx={{ ...bvType.body, color: bvColor.ink, ...tabular }}>
              {row.imported ? (
                <>
                  {row.imported.count.toLocaleString()} · {row.imported.listDate || 'no date'}
                  <Box sx={{ ...bvType.caption, color: fresh ? bvColor.ink3 : bvColor.ink }}>
                    {fresh ? 'Fresh' : 'Stale (>180 days)'}
                  </Box>
                </>
              ) : (
                <Box sx={{ ...bvType.caption, color: bvColor.ink3 }}>Not imported</Box>
              )}
            </Box>
          </Box>
          <Box>
            <Box sx={{ ...bvType.eyebrow, display: { xs: 'block', md: 'none' }, mb: '2px' }}>Open claims</Box>
            {canExpand ? (
              <ButtonBase
                onClick={() => setExpandedBrand(expanded ? null : row.brandId)}
                aria-expanded={expanded}
                aria-label={`${row.brandName} store claims`}
                sx={{
                  ...bvType.body,
                  color: bvColor.ink,
                  ...tabular,
                  justifyContent: 'flex-start',
                  gap: '4px',
                  borderRadius: '6px',
                  whiteSpace: 'nowrap',
                }}
              >
                {`${open} open`}
                {row.claims.verified ? ` · ${row.claims.verified} verified` : ''}
                <IconChevron size={12} dir={expanded ? 'up' : 'down'} />
              </ButtonBase>
            ) : (
              <Box sx={{ ...bvType.body, color: bvColor.ink, whiteSpace: 'nowrap', ...tabular }}>0 open</Box>
            )}
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, minmax(0,1fr))', md: ACTIONS_GRID },
              gap: '6px',
              minWidth: { xs: 0, md: 348 },
              alignItems: 'center',
            }}
          >
            <BvButton
              size="sm"
              disabled={!row.fetchable || busyBrand === row.brandId}
              disabledReason={
                busyBrand === row.brandId
                  ? 'Another action on this brand is still running.'
                  : 'This brand has no list Motzu can download automatically. Use Upload instead.'
              }
              onClick={() => fetchMutation.mutate(row.brandId)}
              sx={{ width: '100%' }}
            >
              Fetch
            </BvButton>
            <BvButton
              size="sm"
              disabled={!row.uploadable}
              disabledReason="This brand publishes no dealer list, so there is nothing to upload."
              onClick={() => {
                setFile(null);
                setListDate('');
                setUploadFor(row);
              }}
              sx={{ width: '100%' }}
            >
              Upload
            </BvButton>
            <BvButton
              size="sm"
              disabled={!row.claimTime || busyBrand === row.brandId}
              disabledReason={
                busyBrand === row.brandId
                  ? 'Another action on this brand is still running.'
                  : 'This brand has no live store locator to test.'
              }
              aria-label={`Test lookup for ${row.brandName}: tries the store locator with a sample store, changes nothing`}
              onClick={() => probeMutation.mutate(row.brandId)}
              sx={{ width: '100%' }}
            >
              Test lookup
            </BvButton>
            <BvButton
              size="sm"
              tone="primary"
              disabled={!canCheck || busyBrand === row.brandId}
              disabledReason={
                busyBrand === row.brandId
                  ? 'Another action on this brand is still running.'
                  : 'Needs an imported dealer list or a live store locator for this brand. Fetch or upload a list first.'
              }
              onClick={() => checkMutation.mutate(row.brandId)}
              sx={{ width: '100%' }}
            >
              Check stores
            </BvButton>
          </Box>
        </Box>
        {expanded && (
          <Box sx={{ px: '16px', pb: '14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {row.claimTime && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <Box sx={{ ...bvType.body, color: bvColor.ink }}>Sample test store: Competitive Cameras, Dallas TX</Box>
                <StatusChip
                  label={
                    probe
                      ? probe.matched
                        ? `Test matched · ${probe.candidate?.name || 'Competitive Cameras'}`
                        : probe.attempt === 'unavailable'
                          ? 'Test could not check'
                          : 'Test found no match'
                      : 'Not run yet'
                  }
                  tone={probe ? (probe.matched ? 'positive' : 'caution') : 'info'}
                  variant="outlined"
                />
                {probe && !probe.matched && (
                  <Box sx={{ ...bvType.caption, color: bvColor.ink3 }}>{probe.notes}</Box>
                )}
              </Box>
            )}
            {lastCheck[row.brandId]?.listFetchError && (
              <InlineMessage severity="warning">
                Could not fetch the published dealer list. {lastCheck[row.brandId]?.listFetchError} Store rows below are from the live locator, or could not be checked. A failed fetch is not a no-match.
              </InlineMessage>
            )}
            {stores.length === 0 && (
              <Box sx={{ ...bvType.caption, color: bvColor.ink3 }}>
                {row.noRegistry
                  ? 'No published list or locator for this brand. Stores verify with proof of authorization reviewed by an admin.'
                  : 'No Motzu store has claimed this brand yet. The test lookup uses a sample store and does not add a claim.'}
              </Box>
            )}
            {stores.map(store => {
              const checkNote = lastCheck[row.brandId]?.results.find(
                result => result.organizationId === store.organizationId,
              );
              return (
                <Box
                  key={store.organizationId}
                  sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}
                >
                  <Box sx={{ ...bvType.body, color: bvColor.ink }}>{store.organizationName}</Box>
                  <StatusChip
                    label={store.status === 'verified' ? 'Verified' : store.status === 'pending' ? 'Pending' : 'Open'}
                    tone={store.status === 'verified' ? 'positive' : store.status === 'pending' ? 'caution' : 'neutral'}
                    variant="outlined"
                  />
                  {checkNote?.outcome === 'unavailable' && store.status !== 'verified' && (
                    <StatusChip label="Could not check" tone="caution" variant="outlined" />
                  )}
                  {checkNote && store.status !== 'verified' && (
                    <Box sx={{ ...bvType.caption, color: bvColor.ink3 }}>{checkNote.notes}</Box>
                  )}
                  {checkNote?.partialMatch && store.status !== 'verified' && (
                    <BvButton
                      size="sm"
                      disabled={approveMutation.isPending}
                      onClick={() =>
                        setApproveTarget({
                          brandId: row.brandId,
                          organizationId: store.organizationId,
                          organizationName: store.organizationName,
                          kind: 'partial',
                          notes: `Approved partial list match: ${store.organizationName}`,
                        })
                      }
                    >
                      Approve match
                    </BvButton>
                  )}
                  {store.staleListMatch && store.status !== 'verified' && (
                    <>
                      <StatusChip
                        label={`Stale list · ${store.staleListMatch.listAge}d`}
                        tone="caution"
                        variant="outlined"
                      />
                      <BvButton
                        size="sm"
                        disabled={approveMutation.isPending}
                        onClick={() =>
                          setApproveTarget({
                            brandId: row.brandId,
                            organizationId: store.organizationId,
                            organizationName: store.organizationName,
                            kind: 'stale',
                          })
                        }
                      >
                        Approve match
                      </BvButton>
                    </>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box sx={bvPageSx}>
      <Box sx={bvContentSx}>
        <BvPageHeader
          title="Dealer lists"
          description="How each brand lets us confirm that a store is an authorized dealer. We check the brand’s dealer list first (if it is under 6 months old), then the brand’s own store finder. Brands that share a finder, such as Panasonic and Lumix, appear as one row; stores still claim each brand separately. A brand with neither is “proof only”. “Test lookup” tries the finder with a sample store and changes nothing."
        />

        <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center', mt: '22px', flexWrap: 'wrap' }}>
          <BvSearchInput
            value={query}
            onChange={setQuery}
            placeholder="Search brands"
            ariaLabel="Search dealer lists"
          />
          <Box role="group" aria-label="Filter dealer lists" sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {([
              { id: 'all', label: 'All brands' },
              { id: 'ready', label: 'List or locator' },
              { id: 'ingested', label: 'Ingested brands' },
              { id: 'letter', label: 'Proof only' },
            ] as const).map(pill => (
              <ButtonBase
                key={pill.id}
                aria-pressed={filter === pill.id}
                onClick={() => setFilter(pill.id)}
                sx={{
                  height: 28,
                  px: '12px',
                  borderRadius: `${bvRadius.pill}px`,
                  ...bvType.buttonSmall,
                  color: filter === pill.id ? bvColor.accentHover : bvColor.ink3,
                  bgcolor: filter === pill.id ? bvColor.surfaceTint : bvColor.surface,
                  border: `1px solid ${filter === pill.id ? bvColor.accent : bvColor.border}`,
                  transition: bvTransition,
                  '&:hover': { borderColor: bvColor.accent },
                  '&:focus-visible': { outline: `2px solid ${bvColor.accent}`, outlineOffset: 1 },
                }}
              >
                {pill.label}
              </ButtonBase>
            ))}
          </Box>
          <Box sx={{ flex: 1 }} />
          {/* An action, not a filter: a real button, set apart from the chips. */}
          <BvButton
            tone="primary"
            disabled={sweepMutation.isPending || importedCount === 0}
            disabledReason="No dealer list has been imported yet. Fetch or upload one first."
            onClick={() => sweepMutation.mutate()}
          >
            {sweepMutation.isPending ? 'Checking…' : 'Check all stores'}
          </BvButton>
        </Box>

        {loadError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {(loadError as Error).message || 'Could not load dealer lists'}
          </Alert>
        )}

        <Box sx={{ ...bvType.caption, color: bvColor.ink3, mt: '12px' }}>
          <strong>Proof only</strong> means the brand publishes no dealer list or finder we can use. Those stores send
          proof of authorization (a dealer-portal screenshot, or a message from the brand or distributor), and an admin
          reviews it.
        </Box>

        <Box sx={{ ...bvCardSx, mt: '10px', overflowX: 'auto' }}>
          <Box
            sx={{
              display: { xs: 'none', md: 'grid' },
              gridTemplateColumns: { xs: '1fr', md: GRID },
              gap: '12px',
              px: '16px',
              py: '10px',
              borderBottom: `1px solid ${bvColor.borderSubtle}`,
              ...bvType.eyebrow,
              color: bvColor.ink3,
            }}
          >
            <Box>Brand</Box>
            <Box>How we verify</Box>
            <Box>Imported list</Box>
            <Box>Open claims</Box>
            <Box>Actions</Box>
          </Box>

          {isLoading &&
            [0, 1, 2, 3, 4, 5].map(index => (
              <Box
                key={index}
                aria-hidden
                data-testid="dealer-list-row-skeleton"
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'minmax(0,1fr)', md: GRID },
                  gap: '12px',
                  px: '16px',
                  py: '14px',
                  alignItems: 'center',
                  borderBottom: `1px solid ${bvColor.borderSubtle}`,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Skeleton variant="rounded" animation="wave" width={32} height={32} sx={{ bgcolor: bvColor.borderSubtle, flexShrink: 0 }} />
                  <Skeleton variant="text" animation="wave" width="55%" height={18} sx={{ bgcolor: bvColor.borderSubtle }} />
                </Box>
                <Skeleton variant="text" animation="wave" width="60%" height={18} sx={{ bgcolor: bvColor.borderSubtle }} />
                <Skeleton variant="text" animation="wave" width="50%" height={18} sx={{ bgcolor: bvColor.borderSubtle }} />
                <Skeleton variant="text" animation="wave" width="40%" height={18} sx={{ bgcolor: bvColor.borderSubtle }} />
                <Skeleton variant="rounded" animation="wave" height={28} sx={{ bgcolor: bvColor.borderSubtle }} />
              </Box>
            ))}
          {!isLoading && rows.length === 0 && (
            <BvEmptyState headline="No dealer sources" note="Nothing matches that filter." />
          )}
          {registryRows.map(row => renderRow(row))}
          {fallbackRows.length > 0 && (
            <Box
              sx={{
                px: '16px',
                py: '10px',
                ...bvType.eyebrow,
                color: bvColor.ink3,
                borderTop: `1px solid ${bvColor.borderSubtle}`,
              }}
            >
              Proof only — no published list ({fallbackRows.length})
            </Box>
          )}
          {fallbackRows.map(row => renderRow(row))}
        </Box>
      </Box>

      <FormDialog
        open={!!uploadFor}
        title={`Upload the ${uploadFor?.brandName || ''} dealer list`}
        onClose={() => setUploadFor(null)}
        onSubmit={() => uploadMutation.mutate()}
        submitLabel={uploadMutation.isPending ? 'Importing…' : 'Import'}
        submitDisabled={!file || uploadMutation.isPending}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          PDF as the brand publishes it, or a CSV with a name column and optional city, state, website and type columns. This replaces the brand’s current rows.
        </Typography>
        <BvButton
          onClick={() => document.getElementById('dealer-list-file')?.click()}
        >
          {file ? file.name : 'Choose file (PDF, CSV or text)'}
        </BvButton>
        <input
          id="dealer-list-file"
          hidden
          type="file"
          accept="application/pdf,.csv,text/plain,.txt,text/html,.html"
          onChange={e => setFile(e.target.files?.[0] || null)}
        />
        <DateField
          label="List date"
          value={listDate}
          onChange={setListDate}
          helperText="Leave blank to read it from the document"
          size="medium"
          sx={{ width: '100%', mt: 2 }}
        />
      </FormDialog>

      <ConfirmDialog
        open={!!approveTarget}
        title={approveTarget?.kind === 'partial' ? 'Approve partial match' : 'Approve stale list match'}
        message={
          approveTarget?.kind === 'partial'
            ? `Verify ${approveTarget.organizationName}? The dealer list has a close name with a different spelling. This does not verify on its own.`
            : approveTarget
              ? `Verify ${approveTarget.organizationName} from the imported list even though that list is older than 180 days?`
              : ''
        }
        confirmLabel={approveMutation.isPending ? 'Approving…' : 'Approve match'}
        loading={approveMutation.isPending}
        onConfirm={() => {
          if (approveTarget) {
            approveMutation.mutate({
              brandId: approveTarget.brandId,
              organizationId: approveTarget.organizationId,
              kind: approveTarget.kind,
              notes: approveTarget.notes,
            });
          }
        }}
        onClose={() => {
          if (!approveMutation.isPending) setApproveTarget(null);
        }}
      />
    </Box>
  );
};

export default BrandVendorsDealerListsPage;
